import { useEffect, useRef } from 'react';

/**
 * Client-only "spinning of death" recovery.
 *
 * When a table renders but never hydrates — no `gotTexas` arrives, so
 * `game.tabletype` stays empty and the felt shows only the spinning HJ chip
 * forever — force ONE reconnect. A fresh connectionId is treated by the server as
 * a NEW subscription, which triggers `publishInitialSnapshot` → the felt finally
 * hydrates. This is a MITIGATION for the RCA item-C fan-out race (a `TABLE_UPDATE`
 * fanned out before the new connection's subscriber row was visible — no
 * `ConsistentRead`, no re-send). The durable, parity-correct fix is server-side
 * snapshot-on-subscribe robustness and is intentionally deferred to a separate
 * branch; this only papers over the client symptom.
 *
 * Safety (this is exactly why the earlier hydration watchdog was reverted — it
 * looped and tore the socket down mid-load):
 *   - Fires AT MOST ONCE per gameID (`attemptedRef`), so even if the fresh
 *     connection STILL doesn't hydrate there is no reconnect loop.
 *   - Jittered delay, comfortably longer than a healthy join's snapshot latency,
 *     so a normal (slow-but-fine) join never triggers a reconnect.
 *   - Module-level cooldown, so several stuck tables / rapid re-opens can't fan a
 *     reconnect storm at scale.
 *   - Cancels the instant the felt hydrates.
 *   - Deps are [gameID, hydrated, enabled] — NOT the socket — and `reconnect` is
 *     read through a ref, so a reconnect (which swaps the socket proxy) can never
 *     retrigger this effect.
 *
 * Applies to ALL views — single-table AND every grid cell (mobile
 * portrait/landscape included; those are the same GameInner). The reconnect is
 * global (both sockets), but that's fine in grid: the per-gameID one-shot guard
 * plus the shared module-level cooldown collapse several stuck cells into a SINGLE
 * reconnect, which re-hydrates every open table at once — no per-cell storm. The
 * `enabled` flag stays a general kill-switch (e.g. guests / disabled contexts),
 * not a view gate.
 */

// Shared across every hook instance so concurrent stuck tables coalesce onto one
// reconnect. Exported reset is for tests only (module state persists otherwise).
let lastForcedReconnectAtMs = 0;
const FORCED_RECONNECT_COOLDOWN_MS = 20000;

export function __resetFeltHydrationRecovery(): void {
  lastForcedReconnectAtMs = 0;
}

export function useFeltHydrationRecovery(
  gameID: string | undefined,
  hydrated: boolean,
  reconnect: () => void,
  enabled = true
): void {
  // Read the latest reconnect through a ref so an unstable callback identity
  // (the context recreates it) can't reset the timer every render.
  const reconnectRef = useRef(reconnect);
  reconnectRef.current = reconnect;

  // Tables we've already forced one reconnect for — guards against a loop.
  const attemptedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!enabled || !gameID) return;
    if (hydrated) {
      // Felt is up — clear the one-shot guard so a LATER re-open of the same
      // table that goes stuck again can still recover.
      attemptedRef.current.delete(gameID);
      return;
    }
    if (attemptedRef.current.has(gameID)) return; // already tried once for this table

    // 7–10s: well above a healthy join's snapshot latency (~1–2s), so normal
    // joins never reconnect; only a genuinely stuck felt reaches the timeout.
    const delay = 7000 + Math.floor(Math.random() * 3000);
    const id = window.setTimeout(() => {
      if (attemptedRef.current.has(gameID)) return;
      const nowMs = Date.now();
      if (nowMs - lastForcedReconnectAtMs < FORCED_RECONNECT_COOLDOWN_MS) {
        // Another table just forced a reconnect (which re-subscribes everything);
        // mark this one attempted and let that reconnect hydrate us too.
        attemptedRef.current.add(gameID);
        return;
      }
      attemptedRef.current.add(gameID);
      lastForcedReconnectAtMs = nowMs;
      // eslint-disable-next-line no-console
      console.log('[FeltHydration] table', gameID, 'un-hydrated — forcing one reconnect');
      reconnectRef.current();
    }, delay);
    return () => window.clearTimeout(id);
  }, [gameID, hydrated, enabled]);
}
